import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ENDPOINTS } from '../../enums/endpoints.enum';
import { OdataResponse } from '../../interfaces/online-only.interface';
import {
    InputFieldPropertyModel,
    ODataInputFieldPropertyModel,
} from '../../models/low-level/input-field-property.model';
import { UriUtilitiesService } from '../../services/uri-utilities/uri-utilities.service';
import { InputFieldPropertyState } from '../../state/input-field-properties';
import { isDefined } from '../../utilities/is-defined';
import { ODataUtilities } from '../../utilities/odata-utilities';
import { UserMasterInstanceToken, UserMasterInterface } from '../../interfaces/user-master.interface';
import {
    inputFieldPropertiesToModels,
    odataToInputFieldPropertyState,
} from '../../state/input-field-properties/input-field-properties.converters';
import { OdataInputFieldPropertiesServiceInterface } from './odata-input-field-properties.interface';

@Injectable({
    providedIn: 'root',
})
export class OdataInputFieldPropertiesService implements OdataInputFieldPropertiesServiceInterface {
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly uriUtilities: UriUtilitiesService,
        private readonly http: HttpClient,
        @Inject(UserMasterInstanceToken) private readonly userMasterService: UserMasterInterface,
    ) {}

    /**
     * Fetch the InputFieldProperty set and assemble the store shape directly from the OData
     * payload. This is the primary read: the store is the consumer that survives the migration,
     * so the legacy models are derived from it rather than the other way round.
     *
     * @returns the properties in their serializable store shape
     */
    public getInputFieldPropertiesState(): Observable<InputFieldPropertyState[]> {
        let query = this.userMasterService.setSalesOrgAndPlantForUrl(
            this.uriUtilities.getUriForContent() + ENDPOINTS.InputFieldProperties,
        );
        query = this.uriUtilities.appendParameter(query, '$format=json');

        return this.http.get<OdataResponse<ODataInputFieldPropertyModel>>(query).pipe(
            map((response) => {
                return ODataUtilities.transformResponseDataToArray(response.d)
                    .map((value: ODataInputFieldPropertyModel) => odataToInputFieldPropertyState(value))
                    .filter(isDefined);
            }),
            catchError((err) => {
                this.log.error('getInputFieldPropertiesState() - request error:  ' + JSON.stringify(err));
                return throwError(err);
            }),
        );
    }

    /**
     * Same backend data as {@link getInputFieldPropertiesState}, wrapped back into legacy model
     * instances for the consumers that still expect them.
     *
     * @returns the properties as {@link InputFieldPropertyModel} instances
     */
    public getInputFieldPropertySet(): Observable<InputFieldPropertyModel[]> {
        return this.getInputFieldPropertiesState().pipe(map(inputFieldPropertiesToModels));
    }
}
